import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Monitor, Tablet, LogOut, Fingerprint, RefreshCw, Users } from "lucide-react";
import { listarDispositivos, cerrarDispositivo, cerrarOtrosDispositivos, renombrarDispositivo } from "@/lib/passkeys";

const icono = (nombre) => {
  const n = (nombre || "").toLowerCase();
  if (n.includes("android") || n.includes("iphone")) return Smartphone;
  if (n.includes("ipad")) return Tablet;
  return Monitor;
};

const cuando = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  if (min < 1440) return `hace ${Math.round(min / 60)} h`;
  if (min < 2880) return "ayer";
  return d.toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
};

/** Equipos donde la cuenta quedó conectada. `oscuro` para la app móvil. */
export const DispositivosLista = ({ oscuro = false, puedeVerTodos = false }) => {
  const [rows, setRows] = useState(null);
  const [todos, setTodos] = useState(false);
  const [busy, setBusy] = useState(null);
  const volverAlLogin = oscuro ? "/movil" : "/login";

  const cargar = useCallback(async () => {
    try {
      const d = await listarDispositivos(todos);
      setRows(d.dispositivos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los equipos");
      setRows([]);
    }
  }, [todos]);

  useEffect(() => { cargar(); }, [cargar]);

  const cerrar = async (d) => {
    if (d.actual && !window.confirm("Es el equipo que estás usando: se cerrará tu sesión aquí. ¿Continuar?")) return;
    setBusy(d.familia);
    try {
      await cerrarDispositivo(d.familia);
      toast.success(`Sesión cerrada en ${d.nombre}`);
      if (d.actual) { window.location.href = volverAlLogin; return; }
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cerrar ese equipo");
    } finally { setBusy(null); }
  };

  const renombrar = async (d) => {
    const nombre = window.prompt("Nombre para reconocer este equipo", d.nombre);
    if (nombre === null) return;
    try { await renombrarDispositivo(d.familia, nombre); cargar(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo renombrar"); }
  };

  const cerrarLosDemas = async () => {
    if (!window.confirm("¿Cerrar la sesión en todos los demás equipos? Este seguirá conectado.")) return;
    try {
      const r = await cerrarOtrosDispositivos();
      toast.success(r.cerrados ? `Cerramos ${r.cerrados} equipo(s)` : "No había otros equipos conectados");
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cerrar"); }
  };

  const card = oscuro
    ? "rounded-2xl border border-white/10 bg-white/[0.04] p-4"
    : "rounded-2xl border border-slate-200 bg-white p-4";
  const sub = oscuro ? "text-slate-400" : "text-slate-500";

  return (
    <div data-testid="dispositivos-lista" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button data-testid="dispositivos-refrescar" onClick={cargar}
                className={`h-9 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 ${oscuro ? "border border-white/10 text-slate-300" : "border border-slate-200 text-slate-600"}`}>
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
        <button data-testid="dispositivos-cerrar-otros" onClick={cerrarLosDemas}
                className="h-9 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 border border-rose-400/40 text-rose-500 hover:bg-rose-500/10">
          <LogOut className="w-3.5 h-3.5" /> Cerrar los demás
        </button>
        {puedeVerTodos && (
          <button data-testid="dispositivos-todos" onClick={() => setTodos((v) => !v)}
                  className={`h-9 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 ${todos
                    ? "bg-sky-500 text-white"
                    : oscuro ? "border border-white/10 text-slate-300" : "border border-slate-200 text-slate-600"}`}>
            <Users className="w-3.5 h-3.5" /> {todos ? "Viendo todo el equipo" : "Ver los de mi personal"}
          </button>
        )}
      </div>

      {rows === null && <p className={`text-sm ${sub}`}>Cargando equipos…</p>}
      {rows?.length === 0 && <p className={`text-sm ${sub}`} data-testid="dispositivos-vacio">No hay equipos conectados.</p>}

      {(rows || []).map((d) => {
        const I = icono(d.nombre);
        return (
          <div key={d.familia} data-testid={`dispositivo-${d.familia}`} className={card}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 ${d.actual
                ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30"
                : oscuro ? "bg-white/5 text-slate-400 border border-white/10" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                <I className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold flex items-center gap-2 flex-wrap">
                  <button onClick={() => renombrar(d)} data-testid={`dispositivo-nombre-${d.familia}`} className="underline-offset-2 hover:underline text-left">
                    {d.nombre}
                  </button>
                  {d.actual && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-600">ESTE EQUIPO</span>}
                  {d.biometria && (
                    <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-500 flex items-center gap-1">
                      <Fingerprint className="w-3 h-3" /> HUELLA
                    </span>
                  )}
                </p>
                {todos && d.usuario && <p className={`text-xs font-semibold ${sub}`}>{d.usuario}</p>}
                <p className={`text-xs mt-0.5 ${sub}`}>
                  Última actividad {cuando(d.ultimo_uso_at)} · ingresó {cuando(d.creado_at)}
                </p>
                {d.ip && <p className={`text-[11px] font-mono mt-0.5 ${sub}`}>IP {d.ip}</p>}
              </div>
              <button data-testid={`dispositivo-cerrar-${d.familia}`} disabled={busy === d.familia}
                      onClick={() => cerrar(d)}
                      className="h-9 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 border border-rose-400/40 text-rose-500 hover:bg-rose-500/10 disabled:opacity-40">
                <LogOut className="w-3.5 h-3.5" /> Cerrar
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DispositivosLista;
